import { useEffect, useRef, useState } from "react";
import { useParams } from "react-router-dom";
import {
  getSigningData,
  sendOtp,
  submitSignature,
  verifyOtp,
  type SigningPageData,
} from "@services/api/signing";

type ScreenState = "loading" | "error" | "otp_verification" | "signing" | "submitting" | "success";

export default function SignScreen() {
  const { token } = useParams<{ token: string }>();
  const [state, setState] = useState<ScreenState>("loading");
  const [data, setData] = useState<SigningPageData | null>(null);
  const [errorMsg, setErrorMsg] = useState<string>("");

  const canvasRef = useRef<HTMLCanvasElement>(null);
  const isDrawingRef = useRef(false);
  const [hasSigned, setHasSigned] = useState(false);
  const [signatureMode, setSignatureMode] = useState<"draw" | "upload">("draw");
  const [uploadedFile, setUploadedFile] = useState<File | null>(null);
  const fileInputRef = useRef<HTMLInputElement>(null);

  // OTP state
  const [otpCode, setOtpCode] = useState("");
  const [otpSending, setOtpSending] = useState(false);
  const [otpVerifying, setOtpVerifying] = useState(false);
  const [otpSent, setOtpSent] = useState(false);
  const [otpError, setOtpError] = useState("");
  const [otpSessionToken, setOtpSessionToken] = useState("");

  useEffect(() => {
    if (!token) {
      setErrorMsg("Lien invalide.");
      setState("error");
      return;
    }
    getSigningData(token)
      .then((d) => {
        setData(d);
        if (d.status === "pending") {
          if (d.requires_otp) {
            setState("otp_verification");
          } else {
            setState("signing");
          }
        } else {
          setErrorMsg(errorMessageForStatus(d.status, d.conductor_name));
          setState("error");
        }
      })
      .catch(() => {
        setErrorMsg("Lien invalide ou expiré.");
        setState("error");
      });
  }, [token]);

  // Canvas drawing logic — use requestAnimationFrame to ensure layout is ready (fixes iOS white screen after OTP)
  useEffect(() => {
    if (state !== "signing") return;
    const rafId = requestAnimationFrame(() => {
      setupCanvas();
    });
    return () => {
      cancelAnimationFrame(rafId);
      if (cleanupRef.current) { cleanupRef.current(); cleanupRef.current = null; }
    };
  }, [state]);

  const cleanupRef = useRef<(() => void) | null>(null);

  const setupCanvas = () => {
    const canvas = canvasRef.current;
    if (!canvas) return;
    const ctx = canvas.getContext("2d");
    if (!ctx) return;

    // Ensure canvas resolution matches display size to fix touch offset on mobile
    const r = canvas.getBoundingClientRect();
    const dpr = window.devicePixelRatio || 1;
    canvas.width = r.width * dpr;
    canvas.height = r.height * dpr;

    // Dynamically map CSS coordinates to canvas pixels at touch time,
    // so that layout changes (address bar hide, viewport resize) don't cause offset
    const getPos = (clientX: number, clientY: number) => {
      const rect = canvas.getBoundingClientRect();
      return {
        x: (clientX - rect.left) * (canvas.width / rect.width),
        y: (clientY - rect.top) * (canvas.height / rect.height),
      };
    };

    const startDraw = (x: number, y: number) => {
      isDrawingRef.current = true;
      ctx.beginPath();
      ctx.moveTo(x, y);
    };
    const draw = (x: number, y: number) => {
      if (!isDrawingRef.current) return;
      ctx.lineTo(x, y);
      ctx.strokeStyle = "#1e293b";
      ctx.lineWidth = 2 * dpr;
      ctx.lineCap = "round";
      ctx.lineJoin = "round";
      ctx.stroke();
      setHasSigned(true);
    };
    const stopDraw = () => { isDrawingRef.current = false; };

    const onMouseDown = (e: MouseEvent) => {
      const { x, y } = getPos(e.clientX, e.clientY);
      startDraw(x, y);
    };
    const onMouseMove = (e: MouseEvent) => {
      const { x, y } = getPos(e.clientX, e.clientY);
      draw(x, y);
    };
    const onTouchStart = (e: TouchEvent) => {
      e.preventDefault();
      const t = e.touches[0];
      const { x, y } = getPos(t.clientX, t.clientY);
      startDraw(x, y);
    };
    const onTouchMove = (e: TouchEvent) => {
      e.preventDefault();
      const t = e.touches[0];
      const { x, y } = getPos(t.clientX, t.clientY);
      draw(x, y);
    };

    canvas.addEventListener("mousedown", onMouseDown);
    canvas.addEventListener("mousemove", onMouseMove);
    canvas.addEventListener("mouseup", stopDraw);
    canvas.addEventListener("mouseleave", stopDraw);
    canvas.addEventListener("touchstart", onTouchStart, { passive: false });
    canvas.addEventListener("touchmove", onTouchMove, { passive: false });
    canvas.addEventListener("touchend", stopDraw);

    // Store cleanup so the outer effect can call it
    if (cleanupRef.current) cleanupRef.current();
    cleanupRef.current = () => {
      canvas.removeEventListener("mousedown", onMouseDown);
      canvas.removeEventListener("mousemove", onMouseMove);
      canvas.removeEventListener("mouseup", stopDraw);
      canvas.removeEventListener("mouseleave", stopDraw);
      canvas.removeEventListener("touchstart", onTouchStart);
      canvas.removeEventListener("touchmove", onTouchMove);
      canvas.removeEventListener("touchend", stopDraw);
    };
  };

  const clearCanvas = () => {
    const canvas = canvasRef.current;
    if (!canvas) return;
    const ctx = canvas.getContext("2d");
    ctx?.clearRect(0, 0, canvas.width, canvas.height);
    setHasSigned(false);
  };

  const handleFileUpload = (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (!file) return;

    // Validate file type
    if (!file.type.startsWith("image/")) {
      setErrorMsg("Veuillez sélectionner une image");
      return;
    }

    // Validate file size (max 5MB)
    if (file.size > 5 * 1024 * 1024) {
      setErrorMsg("La taille maximale est de 5 Mo.");
      return;
    }

    setUploadedFile(file);
    setHasSigned(true);
  };

  const handleSendOtp = async () => {
    if (!token) return;
    setOtpSending(true);
    setOtpError("");
    try {
      const result = await sendOtp(token);
      if (result.success) {
        setOtpSent(true);
      } else {
        setOtpError(otpErrorMessage(result.message));
      }
    } catch {
      setOtpError("Impossible d'envoyer le code. Veuillez réessayer.");
    } finally {
      setOtpSending(false);
    }
  };

  const handleVerifyOtp = async () => {
    if (!token || !otpCode) return;
    setOtpVerifying(true);
    setOtpError("");
    try {
      const result = await verifyOtp(token, otpCode);
      if (result.success) {
        if (result.session_token) {
          setOtpSessionToken(result.session_token);
        }
        setState("signing");
      } else {
        setOtpError(otpErrorMessage(result.message));
      }
    } catch {
      setOtpError("Erreur de vérification. Veuillez réessayer.");
    } finally {
      setOtpVerifying(false);
    }
  };

  const handleConfirm = async () => {
    if (!token) return;

    let file: File;

    if (signatureMode === "draw") {
      if (!canvasRef.current) return;
      const dataUrl = canvasRef.current.toDataURL("image/png");

      // Convert data URL to Blob
      const blob = await (await fetch(dataUrl)).blob();

      // Convert Blob to File
      file = new File([blob], "signature.png", { type: "image/png" });
    } else {
      if (!uploadedFile) return;
      file = uploadedFile;
    }

    setState("submitting");
    try {
      const result = await submitSignature(token, file, otpSessionToken || undefined);
      if (result.success) {
        setState("success");
      } else {
        setErrorMsg(errorMessageForStatus(result.message, data?.conductor_name || ""));
        setState("error");
      }
    } catch {
      setErrorMsg("Une erreur est survenue. Veuillez réessayer.");
      setState("error");
    }
  };

  return (
    <div
      style={{
        minHeight: "100vh",
        background: "#f8fafc",
        fontFamily: "Arial, sans-serif",
        color: "#1e293b",
      }}
    >
      {/* Header */}
      <div
        style={{
          background: "#1e293b",
          padding: "20px 24px",
          display: "flex",
          alignItems: "center",
          gap: 12,
        }}
      >
        <span style={{ color: "white", fontSize: 18, fontWeight: "bold" }}>
          Signature de document
        </span>
      </div>

      <div
        style={{
          maxWidth: 680,
          margin: "0 auto",
          padding: "24px 16px",
          display: "flex",
          flexDirection: "column",
          gap: 20,
        }}
      >
        {/* LOADING */}
        {state === "loading" && (
          <div style={{ textAlign: "center", paddingTop: 60, color: "#64748b" }}>
            Chargement…
          </div>
        )}

        {/* ERROR */}
        {state === "error" && (
          <div
            style={{
              background: "#fff1f2",
              border: "1px solid #fecdd3",
              borderRadius: 12,
              padding: 24,
              textAlign: "center",
            }}
          >
            <div style={{ fontSize: 32, marginBottom: 12 }}>⚠️</div>
            <p style={{ color: "#be123c", fontSize: 16, margin: 0 }}>{errorMsg}</p>
          </div>
        )}

        {/* OTP VERIFICATION */}
        {state === "otp_verification" && data && (
          <>
            {/* Info card */}
            <div
              style={{
                background: "white",
                border: "1px solid #e2e8f0",
                borderRadius: 12,
                padding: 20,
                display: "flex",
                flexDirection: "column",
                gap: 6,
              }}
            >
              <div style={{ fontSize: 18, fontWeight: "bold" }}>{data.doc_title}</div>
              <div style={{ color: "#64748b", fontSize: 14 }}>
                Projet : {data.project_name}
              </div>
              <div style={{ color: "#475569", fontSize: 14, marginTop: 4 }}>
                Signataire : <strong>{data.signer_name}</strong>
              </div>
            </div>

            {/* OTP card */}
            <div
              style={{
                background: "white",
                border: "1px solid #e2e8f0",
                borderRadius: 12,
                padding: 24,
                display: "flex",
                flexDirection: "column",
                gap: 16,
                alignItems: "center",
              }}
            >
              <div style={{ fontSize: 16, fontWeight: "bold", textAlign: "center" }}>
                Vérification d'identité
              </div>
              <p style={{ color: "#64748b", fontSize: 14, textAlign: "center", margin: 0 }}>
                Pour sécuriser votre signature, un code de vérification va être envoyé
                par SMS au numéro {data.signer_phone_masked || "enregistré"}.
              </p>

              {!otpSent ? (
                <button
                  onClick={handleSendOtp}
                  disabled={otpSending}
                  style={{
                    padding: "12px 32px",
                    borderRadius: 8,
                    border: "none",
                    background: otpSending ? "#94a3b8" : "#1e293b",
                    color: "white",
                    cursor: otpSending ? "not-allowed" : "pointer",
                    fontSize: 14,
                    fontWeight: "bold",
                  }}
                >
                  {otpSending ? "Envoi..." : "Envoyer le code"}
                </button>
              ) : (
                <>
                  <p style={{ color: "#16a34a", fontSize: 14, margin: 0 }}>
                    Code envoyé par SMS.
                  </p>
                  <input
                    type="text"
                    inputMode="numeric"
                    maxLength={6}
                    value={otpCode}
                    onChange={(e) => setOtpCode(e.target.value.replace(/\D/g, ""))}
                    placeholder="000000"
                    style={{
                      width: 180,
                      padding: "12px 16px",
                      fontSize: 24,
                      fontWeight: "bold",
                      textAlign: "center",
                      letterSpacing: 8,
                      border: "2px solid #e2e8f0",
                      borderRadius: 8,
                      outline: "none",
                    }}
                  />
                  <div style={{ display: "flex", gap: 10 }}>
                    <button
                      onClick={handleSendOtp}
                      disabled={otpSending}
                      style={{
                        padding: "8px 16px",
                        borderRadius: 8,
                        border: "1px solid #e2e8f0",
                        background: "white",
                        color: "#64748b",
                        cursor: "pointer",
                        fontSize: 13,
                      }}
                    >
                      Renvoyer
                    </button>
                    <button
                      onClick={handleVerifyOtp}
                      disabled={otpCode.length !== 6 || otpVerifying}
                      style={{
                        padding: "10px 28px",
                        borderRadius: 8,
                        border: "none",
                        background: otpCode.length === 6 && !otpVerifying ? "#1e293b" : "#94a3b8",
                        color: "white",
                        cursor: otpCode.length === 6 && !otpVerifying ? "pointer" : "not-allowed",
                        fontSize: 14,
                        fontWeight: "bold",
                      }}
                    >
                      {otpVerifying ? "Vérification..." : "Vérifier"}
                    </button>
                  </div>
                </>
              )}

              {otpError && (
                <p style={{ color: "#be123c", fontSize: 13, margin: 0 }}>{otpError}</p>
              )}
            </div>
          </>
        )}

        {/* SIGNING / SUBMITTING */}
        {(state === "signing" || state === "submitting") && data && (
          <>
            {/* Info card */}
            <div
              style={{
                background: "white",
                border: "1px solid #e2e8f0",
                borderRadius: 12,
                padding: 20,
                display: "flex",
                flexDirection: "column",
                gap: 6,
              }}
            >
              <div style={{ fontSize: 18, fontWeight: "bold" }}>{data.doc_title}</div>
              <div style={{ color: "#64748b", fontSize: 14 }}>
                Projet : {data.project_name}
              </div>
              <div style={{ color: "#64748b", fontSize: 14 }}>
                Demandé par : {data.conductor_name}
              </div>
              <div style={{ color: "#475569", fontSize: 14, marginTop: 4 }}>
                Signataire : <strong>{data.signer_name}</strong>
              </div>
            </div>

            {/* PDF preview */}
            {data.pdf_url && (
              <div
                style={{
                  background: "white",
                  border: "1px solid #e2e8f0",
                  borderRadius: 12,
                  overflow: "hidden",
                }}
              >
                <iframe
                  src={data.pdf_url}
                  style={{ width: "100%", height: 480, border: "none", display: "block" }}
                  title="Document à signer"
                />
              </div>
            )}

            {/* Signature canvas/upload */}
            <div
              style={{
                background: "white",
                border: "1px solid #e2e8f0",
                borderRadius: 12,
                padding: 20,
                display: "flex",
                flexDirection: "column",
                gap: 12,
              }}
            >
              {/* Tabs */}
              <div style={{ display: "flex", gap: 10, borderBottom: "2px solid #e2e8f0" }}>
                <button
                  onClick={() => {
                    setSignatureMode("draw");
                    setUploadedFile(null);
                    setHasSigned(false);
                  }}
                  disabled={state === "submitting"}
                  style={{
                    padding: "10px 20px",
                    border: "none",
                    background: "transparent",
                    borderBottom: signatureMode === "draw" ? "3px solid #1e293b" : "none",
                    color: signatureMode === "draw" ? "#1e293b" : "#64748b",
                    cursor: "pointer",
                    fontSize: 14,
                    fontWeight: signatureMode === "draw" ? "bold" : "normal",
                    marginBottom: "-2px",
                  }}
                >
                  Dessiner
                </button>
                <button
                  onClick={() => {
                    setSignatureMode("upload");
                    clearCanvas();
                    setHasSigned(false);
                  }}
                  disabled={state === "submitting"}
                  style={{
                    padding: "10px 20px",
                    border: "none",
                    background: "transparent",
                    borderBottom: signatureMode === "upload" ? "3px solid #1e293b" : "none",
                    color: signatureMode === "upload" ? "#1e293b" : "#64748b",
                    cursor: "pointer",
                    fontSize: 14,
                    fontWeight: signatureMode === "upload" ? "bold" : "normal",
                    marginBottom: "-2px",
                  }}
                >
                  Télécharger
                </button>
              </div>

              {/* Draw mode */}
              {signatureMode === "draw" && (
                <>
                  <div style={{ fontSize: 14, color: "#64748b" }}>
                    Signez dans le cadre ci-dessous (doigt ou souris)
                  </div>
                  <canvas
                    ref={canvasRef}
                    style={{
                      border: "2px dashed #cbd5e1",
                      borderRadius: 8,
                      background: "#f8fafc",
                      width: "100%",
                      height: 180,
                      touchAction: "none",
                      cursor: state === "submitting" ? "not-allowed" : "crosshair",
                    }}
                  />
                  <div style={{ display: "flex", gap: 10, justifyContent: "flex-end" }}>
                    <button
                      onClick={clearCanvas}
                      disabled={state === "submitting"}
                      style={{
                        padding: "8px 20px",
                        borderRadius: 8,
                        border: "1px solid #e2e8f0",
                        background: "white",
                        color: "#64748b",
                        cursor: "pointer",
                        fontSize: 14,
                      }}
                    >
                      Effacer
                    </button>
                    <button
                      onClick={handleConfirm}
                      disabled={!hasSigned || state === "submitting"}
                      style={{
                        padding: "10px 28px",
                        borderRadius: 8,
                        border: "none",
                        background: hasSigned && state !== "submitting" ? "#1e293b" : "#94a3b8",
                        color: "white",
                        cursor: hasSigned && state !== "submitting" ? "pointer" : "not-allowed",
                        fontSize: 14,
                        fontWeight: "bold",
                      }}
                    >
                      {state === "submitting" ? "Envoi…" : "Confirmer la signature"}
                    </button>
                  </div>
                </>
              )}

              {/* Upload mode */}
              {signatureMode === "upload" && (
                <>
                  <div style={{ fontSize: 14, color: "#64748b" }}>
                    Téléchargez votre signature (image)
                  </div>
                  <input
                    ref={fileInputRef}
                    type="file"
                    accept="image/*"
                    onChange={handleFileUpload}
                    style={{ display: "none" }}
                  />
                  <div
                    onClick={() => !state || state === "signing" ? fileInputRef.current?.click() : null}
                    style={{
                      border: "2px dashed #cbd5e1",
                      borderRadius: 8,
                      background: "#f8fafc",
                      padding: 40,
                      textAlign: "center",
                      cursor: state === "submitting" ? "not-allowed" : "pointer",
                      minHeight: 180,
                      display: "flex",
                      flexDirection: "column",
                      alignItems: "center",
                      justifyContent: "center",
                      gap: 12,
                    }}
                  >
                    {uploadedFile ? (
                      <>
                        <div style={{ fontSize: 48 }}>✓</div>
                        <div style={{ fontSize: 14, color: "#1e293b", fontWeight: 500 }}>
                          {uploadedFile.name}
                        </div>
                        <img
                          src={URL.createObjectURL(uploadedFile)}
                          alt="Signature preview"
                          style={{ maxHeight: 100, maxWidth: "100%", objectFit: "contain" }}
                        />
                        <button
                          onClick={(e) => {
                            e.stopPropagation();
                            setUploadedFile(null);
                            setHasSigned(false);
                            if (fileInputRef.current) {
                              fileInputRef.current.value = "";
                            }
                          }}
                          style={{
                            padding: "6px 16px",
                            borderRadius: 6,
                            border: "1px solid #e2e8f0",
                            background: "white",
                            color: "#64748b",
                            cursor: "pointer",
                            fontSize: 13,
                          }}
                        >
                          Changer
                        </button>
                      </>
                    ) : (
                      <>
                        <svg
                          xmlns="http://www.w3.org/2000/svg"
                          width="48"
                          height="48"
                          viewBox="0 0 24 24"
                          fill="none"
                          stroke="#94a3b8"
                          strokeWidth="2"
                          strokeLinecap="round"
                          strokeLinejoin="round"
                        >
                          <path d="M21 15v4a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2v-4" />
                          <polyline points="17 8 12 3 7 8" />
                          <line x1="12" y1="3" x2="12" y2="15" />
                        </svg>
                        <div style={{ fontSize: 14, color: "#64748b" }}>
                          Cliquez pour télécharger une image
                        </div>
                        <div style={{ fontSize: 12, color: "#94a3b8" }}>
                          PNG, JPG ou WEBP (max. 5 Mo)
                        </div>
                      </>
                    )}
                  </div>
                  <div style={{ display: "flex", gap: 10, justifyContent: "flex-end" }}>
                    <button
                      onClick={handleConfirm}
                      disabled={!uploadedFile || state === "submitting"}
                      style={{
                        padding: "10px 28px",
                        borderRadius: 8,
                        border: "none",
                        background: uploadedFile && state !== "submitting" ? "#1e293b" : "#94a3b8",
                        color: "white",
                        cursor: uploadedFile && state !== "submitting" ? "pointer" : "not-allowed",
                        fontSize: 14,
                        fontWeight: "bold",
                      }}
                    >
                      {state === "submitting" ? "Envoi…" : "Confirmer la signature"}
                    </button>
                  </div>
                </>
              )}
            </div>
          </>
        )}

        {/* SUCCESS */}
        {state === "success" && data && (
          <div
            style={{
              background: "#f0fdf4",
              border: "1px solid #bbf7d0",
              borderRadius: 12,
              padding: 32,
              textAlign: "center",
              display: "flex",
              flexDirection: "column",
              gap: 10,
              alignItems: "center",
            }}
          >
            <div style={{ fontSize: 48 }}>✓</div>
            <div style={{ fontSize: 18, fontWeight: "bold", color: "#15803d" }}>
              Signature enregistrée
            </div>
            <p style={{ color: "#166534", margin: 0 }}>
              Merci {data.signer_name}, votre signature a bien été enregistrée.
            </p>
            <p style={{ color: "#64748b", fontSize: 13, margin: 0 }}>
              {data.doc_title}
            </p>
          </div>
        )}
      </div>
    </div>
  );
}

function otpErrorMessage(message: string): string {
  if (message === "otp_expired") return "Le code a expiré. Veuillez en demander un nouveau.";
  if (message === "max_attempts") return "Trop de tentatives. Veuillez demander un nouveau code.";
  if (message === "no_phone") return "Aucun numéro de téléphone enregistré.";
  if (message === "sms_failed") return "Impossible d'envoyer le SMS. Veuillez réessayer.";
  if (message.startsWith("invalid_code:")) {
    const remaining = message.split(":")[1];
    return `Code incorrect. ${remaining} tentative(s) restante(s).`;
  }
  return "Erreur de vérification.";
}

function errorMessageForStatus(status: string, conductorName: string): string {
  switch (status) {
    case "expired":
      return conductorName
        ? `Ce lien a expiré. Contactez ${conductorName} pour recevoir un nouveau lien.`
        : "Ce lien a expiré.";
    case "signed":
    case "already_signed":
      return "Vous avez déjà signé ce document. Merci !";
    case "superseded":
      return "Ce lien n'est plus valide. Un nouveau lien vous a peut-être été envoyé.";
    default:
      return "Lien invalide ou introuvable.";
  }
}
